/**
 * ImportExportTab Component
 *
 * Story 9-11: CSV Import/Export Functionaliteit
 * Allows admins to export data as CSV and import data with validation.
 */

import { useState, useRef } from 'react';
import { importExportApi, type ImportExportType, type ImportPreviewResult } from '../../../lib/settingsApi';

type ImportableType = 'tasks' | 'competencies' | 'departments' | 'functions';

const EXPORT_TYPES: { type: ImportExportType; label: string; description: string }[] = [
  { type: 'employees', label: 'Medewerkers', description: 'Alle medewerkergegevens met functie en team' },
  { type: 'tasks', label: 'Taken', description: 'Alle taken met wegingen en niveaus' },
  { type: 'competencies', label: 'Competenties', description: 'Alle competenties met types en koppelingen' },
  { type: 'departments', label: 'Teams', description: 'Alle teams' },
  { type: 'functions', label: 'Functies', description: 'Alle functies met koppelingen' },
];

const IMPORT_TYPES: { type: ImportableType; label: string; description: string }[] = [
  { type: 'tasks', label: 'Taken', description: 'Taken importeren of bijwerken' },
  { type: 'competencies', label: 'Competenties', description: 'Competenties importeren of bijwerken' },
  { type: 'departments', label: 'Teams', description: 'Teams importeren of bijwerken' },
  { type: 'functions', label: 'Functies', description: 'Functies importeren of bijwerken' },
];

export function ImportExportTab() {
  const [activeSection, setActiveSection] = useState<'export' | 'import'>('export');
  const [exportingType, setExportingType] = useState<ImportExportType | null>(null);
  const [selectedImportType, setSelectedImportType] = useState<ImportableType>('tasks');
  const [csvData, setCsvData] = useState<string>('');
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: boolean; created: number; updated: number; errors: string[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async (type: ImportExportType) => {
    setExportingType(type);
    try {
      const blob = await importExportApi.exportCsv(type);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExportingType(null);
    }
  };

  const handleDownloadTemplate = async (type: ImportableType) => {
    try {
      const blob = await importExportApi.downloadTemplate(type);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template-${type}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Template download failed:', err);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        setCsvData(text);
        setPreview(null);
        setImportResult(null);
      };
      reader.readAsText(file);
    }
  };

  const handlePreview = async () => {
    if (!csvData.trim()) return;

    setIsPreviewLoading(true);
    setImportResult(null);
    try {
      const result = await importExportApi.previewImport(selectedImportType, csvData);
      setPreview(result);
    } catch (err) {
      console.error('Preview failed:', err);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleImport = async () => {
    if (!csvData.trim() || !preview?.valid) return;

    setIsImporting(true);
    try {
      const result = await importExportApi.executeImport(selectedImportType, csvData);
      setImportResult(result);
      if (result.success) {
        setCsvData('');
        setPreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (err) {
      // Zonder dit blijft een geweigerde import onzichtbaar: de knop stopt met
      // draaien en er verschijnt niets. Sinds de import de actief-status
      // verplicht stelt is een 400 met een leesbare uitleg juist het normale pad
      // voor een bestand dat die kolom mist.
      console.error('Import failed:', err);
      setImportResult({
        success: false,
        created: 0,
        updated: 0,
        errors: [
          err instanceof Error && err.message
            ? err.message
            : 'De import is geweigerd. Controleer of het bestand alle verplichte kolommen bevat.',
        ],
      });
    } finally {
      setIsImporting(false);
    }
  };

  const resetImport = () => {
    setCsvData('');
    setPreview(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with section toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Import / Export</h2>
          <p className="mt-1 text-sm text-gray-500">
            Exporteer data naar CSV of importeer nieuwe gegevens
          </p>
        </div>
        <div className="flex rounded-lg border border-gray-300 p-1">
          <button
            onClick={() => setActiveSection('export')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeSection === 'export'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Export
          </button>
          <button
            onClick={() => setActiveSection('import')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeSection === 'import'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Import
          </button>
        </div>
      </div>

      {/* Export Section */}
      {activeSection === 'export' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900">Data Exporteren</h3>
            <p className="text-sm text-gray-500 mt-1">Download data als CSV bestand</p>
          </div>
          <ul className="divide-y divide-gray-200">
            {EXPORT_TYPES.map(({ type, label, description }) => (
              <li key={type} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{label}</p>
                  <p className="text-sm text-gray-500">{description}</p>
                </div>
                <button
                  onClick={() => handleExport(type)}
                  disabled={exportingType === type}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  {exportingType === type ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Exporteren...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download CSV
                    </>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Import Section */}
      {activeSection === 'import' && (
        <div className="space-y-4">
          {/* Import type selection */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">Data Type Selecteren</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {IMPORT_TYPES.map(({ type, label }) => (
                  <button
                    key={type}
                    onClick={() => {
                      setSelectedImportType(type);
                      resetImport();
                    }}
                    className={`px-4 py-3 border rounded-lg text-sm font-medium transition-colors ${
                      selectedImportType === type
                        ? 'border-blue-600 bg-blue-50 text-blue-600'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-4">
                <button
                  onClick={() => handleDownloadTemplate(selectedImportType)}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download template voor {IMPORT_TYPES.find(t => t.type === selectedImportType)?.label}
                </button>
              </div>
            </div>
          </div>

          {/* File upload */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">CSV Bestand Uploaden</h3>
            </div>
            <div className="p-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer"
                >
                  Bestand selecteren
                </label>
                <p className="mt-2 text-sm text-gray-500">
                  Selecteer een CSV bestand om te importeren
                </p>
              </div>

              {csvData && (
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Bestand geladen ({csvData.split('\n').length - 1} rijen)
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={resetImport}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Wissen
                    </button>
                    <button
                      onClick={handlePreview}
                      disabled={isPreviewLoading}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isPreviewLoading ? 'Valideren...' : 'Valideren & Preview'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Preview results */}
          {preview && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className={`px-6 py-3 border-b ${preview.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <h3 className={`text-sm font-medium ${preview.valid ? 'text-green-900' : 'text-red-900'}`}>
                  {preview.valid ? 'Validatie geslaagd' : 'Validatiefouten gevonden'}
                </h3>
                <p className={`text-sm mt-1 ${preview.valid ? 'text-green-700' : 'text-red-700'}`}>
                  {preview.validRows} van {preview.totalRows} rijen zijn geldig
                </p>
              </div>

              {/* Errors */}
              {preview.errorRows.length > 0 && (
                <div className="p-6 border-b border-gray-200">
                  <h4 className="text-sm font-medium text-red-900 mb-2">Fouten:</h4>
                  <ul className="space-y-1 max-h-40 overflow-y-auto">
                    {preview.errorRows.map(({ row, errors }) => (
                      <li key={row} className="text-sm text-red-700">
                        Rij {row}: {errors.join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Preview table */}
              {preview.preview.length > 0 && (
                <div className="p-6 overflow-x-auto">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Preview (eerste 10 rijen):</h4>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {Object.keys(preview.preview[0]!).map((header) => (
                          <th
                            key={header}
                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {preview.preview.map((row, idx) => (
                        <tr key={idx}>
                          {Object.values(row).map((value, colIdx) => (
                            <td key={colIdx} className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                              {String(value)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Import button */}
              {preview.valid && (
                <div className="px-6 py-4 bg-gray-50 flex justify-end">
                  <button
                    onClick={handleImport}
                    disabled={isImporting}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    {isImporting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Importeren...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Importeren ({preview.validRows} rijen)
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Import result */}
          {importResult && (
            <div className={`border rounded-lg p-4 ${importResult.success ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
              <h3 className={`text-sm font-medium ${importResult.success ? 'text-green-900' : 'text-yellow-900'}`}>
                {importResult.success ? 'Import geslaagd!' : 'Import voltooid met waarschuwingen'}
              </h3>
              <p className={`text-sm mt-1 ${importResult.success ? 'text-green-700' : 'text-yellow-700'}`}>
                {importResult.created} nieuwe records aangemaakt, {importResult.updated} records bijgewerkt
              </p>
              {importResult.errors.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {importResult.errors.map((error, idx) => (
                    <li key={idx} className="text-sm text-yellow-700">
                      {error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Info box */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-900">Over import</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Download eerst de template om het juiste formaat te gebruiken</li>
                  <li>Bestaande records worden bijgewerkt op basis van de naam</li>
                  <li>Alleen de admin rol kan imports uitvoeren</li>
                  <li>Medewerkers kunnen niet via CSV geïmporteerd worden</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
